import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { task } from 'ember-concurrency'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { IntlService } from 'ember-intl'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import Component from '@glimmer/component'
import type SessionService from 'teacher-ui-client/services/session'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { TextArea } from 'teacher-ui-client/components/base/text-area'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import type ContactUsRoute from 'teacher-ui-client/subscription-type/contact-us/route'
import type Owner from '@ember/owner'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Args {
  model: ModelFor<ContactUsRoute>
}

interface Signature {
  Args: Args
}

interface ContactUsFormData {
  name: string
  email: string
  schoolName: string
  schoolCode: string
  additionalLicenses?: string
  contactNumber?: string
  device: string
  problemType: string
  problemDescription: string
}

export class ContactUs extends Component<Signature> {
  @service
  intl!: IntlService

  @service
  authToken!: AuthToken

  @service
  log!: Log

  @service
  session!: SessionService

  @tracked formData: ContactUsFormData

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.formData = { ...this.defaultFormData }
  }

  contactUs = task(
    { drop: true },
    async (contactData: ContactUsFormData, { subscriptionType }: { subscriptionType: SubscriptionType }) => {
      const url = this.buildUrl(subscriptionType)

      const data = {
        name: contactData.name,
        email: contactData.email,
        contact_number: contactData.contactNumber,
        device: contactData.device === '' ? undefined : contactData.device,
        additional_licenses: contactData.additionalLicenses,
        problem_type: contactData.problemType === '' ? undefined : contactData.problemType,
        problem_description: contactData.problemDescription,
        school_name: contactData.schoolName,
        school_code: contactData.schoolCode,
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: this._authorization,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      if (response.ok) {
        return { ok: true }
      }

      if (response.status !== 0) {
        // don't log a client network issue.
        this.log.error('Failed to submit contact us form', response.statusText)
      }

      return { ok: false }
    },
  )

  get defaultFormData(): ContactUsFormData {
    return {
      device: '',
      problemType: '',
      problemDescription: '',
      name: this.session.currentTeacher.login,
      email: this.session.currentTeacher.email,
      schoolName: this.session.currentSchool.name,
      schoolCode: this.session.currentSchool.schoolCode,
    }
  }

  get themeKey() {
    return subscriptionTypeToThemeKey(this.args.model.subscriptionType)
  }

  get isSubmitDisabled() {
    return this.contactUs.isRunning
  }

  get contactDetails() {
    if (this.args.model.partnerDetailsForSub) {
      const { schoolSupport, schoolSales } = this.args.model.partnerDetailsForSub
      return [
        { title: this.intl.t('pages.contactUs.contactDetails.supportTitle'), ...schoolSupport },
        { title: this.intl.t('pages.contactUs.contactDetails.salesTitle'), ...schoolSales },
      ]
    }
    return []
  }

  get faqUrl() {
    const urls = {
      // yep, as of writing its faq vs faqs
      [SubscriptionType.Reading]: 'https://readingeggs.com/schools/faqs/',
      [SubscriptionType.Maths]: 'https://mathseeds.com/schools/faq/',
      [SubscriptionType.Writing]: null,
    }
    return urls[this.args.model.subscriptionType]
  }

  get problemTypeOptions() {
    return [
      {
        value: 'register_and_add_students',
        label: this.intl.t('pages.contactUs.fields.problemType.options.registerAndAddStudents'),
      },
      {
        value: 'how_to_get_free_trial',
        label: this.intl.t('pages.contactUs.fields.problemType.options.howToGetFreeTrial'),
      },
      {
        value: 'dont_know_where_to_add_school_code',
        label: this.intl.t('pages.contactUs.fields.problemType.options.dontKnowWhereToAddSchoolCode'),
      },
      {
        value: 'purchase_subscription',
        label: this.intl.t('pages.contactUs.fields.problemType.options.purchaseSubscription'),
      },
      { value: 'general', label: this.intl.t('pages.contactUs.fields.problemType.options.general') },
      { value: 'other', label: this.intl.t('pages.contactUs.fields.problemType.options.other') },
    ]
  }

  get deviceOptions() {
    return [
      { value: 'ipad', label: this.intl.t('pages.contactUs.fields.device.options.iPad') },
      { value: 'android_tablet', label: this.intl.t('pages.contactUs.fields.device.options.androidTablet') },
      { value: 'computer', label: this.intl.t('pages.contactUs.fields.device.options.computer') },
    ]
  }

  updateName = (e: Event) => {
    const name = (e.target as HTMLInputElement | null)?.value
    if (!name) return
    this.formData = { ...this.formData, name }
  }

  updateEmail = (e: Event) => {
    const email = (e.target as HTMLInputElement | null)?.value
    if (!email) return
    this.formData = { ...this.formData, email }
  }

  updateContactNumber = (e: Event) => {
    const contactNumber = (e.target as HTMLInputElement | null)?.value
    if (!contactNumber) return
    this.formData = { ...this.formData, contactNumber }
  }

  updateDevice = (e: Event) => {
    const device = (e.target as HTMLSelectElement | null)?.value
    if (!device) return
    this.formData = { ...this.formData, device }
  }

  updateAdditionalLicenses = (e: Event) => {
    const additionalLicenses = (e.target as HTMLInputElement | null)?.value
    if (!additionalLicenses) return
    this.formData = { ...this.formData, additionalLicenses }
  }

  updateProblemType = (e: Event) => {
    const problemType = (e.target as HTMLSelectElement | null)?.value
    if (!problemType) return
    this.formData = { ...this.formData, problemType }
  }

  updateProblemDescription = (e: Event) => {
    const problemDescription = (e.target as HTMLTextAreaElement | null)?.value
    if (!problemDescription) return
    this.formData = { ...this.formData, problemDescription }
  }

  /* This regex pattern only passes if at least one non-whitespace character is entered, it should be provided to the
   * pattern attr of a required form element.
   * The regex here is different to what is normally used in JS, we need to provide it to the form element as a string
   */
  readonly requiredRegex = '.*\\S.*'

  get showSuccessMessage() {
    return this.contactUs.last?.value?.ok === true
  }

  get showErrorMessage() {
    return this.contactUs.last?.value?.ok === false
  }

  get _authorization() {
    return this.authToken.token ?? ''
  }

  private buildUrl(subscriptionType: SubscriptionType) {
    const productPaths = {
      [SubscriptionType.Reading]: 're',
      [SubscriptionType.Maths]: 'ms',
      [SubscriptionType.Writing]: 'wl',
    }
    return join(config.readingEggsV1Url, 'teacher_ui', productPaths[subscriptionType], 'contact-us').toString()
  }

  @action
  async submitAction(event: { preventDefault: () => void }) {
    event.preventDefault()
    const { ok } = await this.contactUs.perform(this.formData, { subscriptionType: this.args.model.subscriptionType })
    if (ok) this.resetForm()
  }

  @action
  resetForm() {
    this.formData = { ...this.defaultFormData }
  }

  <template>
    <div class="grow">
      <PageHeader>
        <:title>
          <h1>
            {{t "pages.contactUs.title"}}
          </h1>
        </:title>
      </PageHeader>
      <div class="mx-auto max-w-(--breakpoint-lg)">
        <div class="my-8">
          {{#if this.showSuccessMessage}}
            <InlineAlert data-test-alert-success @type={{AlertType.Success}}>
              <InlineAlertSubtitle>
                {{t "pages.contactUs.success"}}
              </InlineAlertSubtitle>
            </InlineAlert>
          {{/if}}
          {{#if this.showErrorMessage}}
            <InlineAlert data-test-alert-error @type={{AlertType.Critical}}>
              <InlineAlertSubtitle>
                {{t "pages.contactUs.error"}}
              </InlineAlertSubtitle>
            </InlineAlert>
          {{/if}}
        </div>
        <p class="mb-3">{{t "pages.contactUs.callToAction"}}</p>
        {{#if this.faqUrl}}
          <p class="mb-3">{{t "pages.contactUs.checkFAQ" faqUrl=this.faqUrl htmlSafe=true}}</p>
        {{/if}}
        <div class="my-8 flex flex-wrap gap-8">
          {{#each this.contactDetails as |details|}}
            <div data-test-contact-details>
              <h3 data-test-title={{details.title}} class="mb-4">{{details.title}}</h3>
              <dl>
                <dt data-test-email class="mt-2 font-semibold">{{t "pages.contactUs.contactDetails.email"}}</dt>
                <dd>
                  <a href="mailto:{{details.email}}">{{details.email}}</a>
                </dd>
                <dt data-test-phone class="mt-2 font-semibold">{{t "pages.contactUs.contactDetails.phone"}}</dt>
                <dd>
                  <a href="tel:{{details.phone}}">{{details.phone}}</a>
                  {{if details.phoneTollFree (t "pages.contactUs.contactDetails.tollFree")}}
                </dd>
                <dd>{{details.phoneHours}}</dd>
                {{#if details.address}}
                  <dt data-test-address class="mt-2 font-semibold">{{t "pages.contactUs.contactDetails.address"}}</dt>
                  <dd>{{details.address}}</dd>
                {{/if}}
              </dl>
            </div>
          {{/each}}
        </div>
        <form
          data-test-contact-form
          class="my-8 grid grid-cols-1 gap-3 lg:grid-cols-4"
          {{on "submit" this.submitAction}}
        >
          <label class="font-bold">
            *
            {{t "pages.contactUs.fields.name.label"}}
            <InputText
              data-test-contact-form-name-input
              name="contact[name]"
              required="required"
              pattern={{this.requiredRegex}}
              title={{t "pages.contactUs.fields.name.patternMessage"}}
              type="text"
              value={{this.formData.name}}
              class="mt-2 w-full px-4 py-3 font-normal"
              @themeKey={{this.themeKey}}
              {{on "input" this.updateName}}
            />
          </label>
          <label class="font-bold">
            {{t "pages.contactUs.fields.email.label"}}
            <InputText
              data-test-contact-form-email-input
              name="contact[email]"
              type="email"
              value={{this.formData.email}}
              class="mt-2 w-full px-4 py-3 font-normal"
              @themeKey={{this.themeKey}}
              {{on "input" this.updateEmail}}
            />
          </label>
          <label class="font-bold">
            {{t "pages.contactUs.fields.contactNumber.label"}}
            <InputText
              data-test-contact-form-contact-number-input
              name="contact[contactNumber]"
              type="tel"
              value={{this.formData.contactNumber}}
              class="mt-2 w-full px-4 py-3 font-normal"
              @themeKey={{this.themeKey}}
              {{on "input" this.updateContactNumber}}
            />
          </label>
          <label class="font-bold">
            {{t "pages.contactUs.fields.device.label"}}
            <BaseSelect
              data-test-contact-form-device-select
              class="mt-2 w-full px-4 py-3 font-normal"
              name="contact[device]"
              @themeKey={{this.themeKey}}
              value={{this.formData.device}}
              {{on "input" this.updateDevice}}
            >
              <option value="">{{t "pages.contactUs.fields.device.placeholder"}}</option>
              {{#each this.deviceOptions as |opt|}}
                <option value={{opt.value}}>{{opt.label}}</option>
              {{/each}}
            </BaseSelect>
          </label>
          <label class="font-bold">
            {{t "pages.contactUs.fields.schoolName.label"}}
            <InputText
              data-test-contact-form-school-name
              name="contact[schoolName]"
              type="text"
              value={{this.formData.schoolName}}
              disabled={{true}}
              class="mt-2 w-full px-4 py-3 font-normal"
              @themeKey={{this.themeKey}}
            />
          </label>
          <label class="font-bold">
            {{t "pages.contactUs.fields.schoolCode.label"}}
            <InputText
              data-test-contact-form-school-code
              name="contact[schoolCode]"
              type="text"
              value={{this.formData.schoolCode}}
              disabled={{true}}
              class="mt-2 w-full px-4 py-3 font-normal"
              @themeKey={{this.themeKey}}
            />
          </label>
          <label class="font-bold lg:col-span-2">
            {{t "pages.contactUs.fields.additionalLicenses.label"}}
            <InputText
              data-test-contact-form-additional-licenses
              name="contact[additionalLicenses]"
              type="text"
              value={{this.formData.additionalLicenses}}
              class="mt-2 w-full px-4 py-3 font-normal"
              @themeKey={{this.themeKey}}
              {{on "input" this.updateAdditionalLicenses}}
            />
          </label>
          <label class="font-bold lg:col-span-4">
            *
            {{t "pages.contactUs.fields.problemType.label"}}
            <BaseSelect
              data-test-contact-form-query-type-select
              name="contact[problemType]"
              class="mt-2 w-full px-4 py-3 font-normal"
              required="required"
              value={{this.formData.problemType}}
              @themeKey={{this.themeKey}}
              {{on "input" this.updateProblemType}}
            >
              <option value="">{{t "pages.contactUs.fields.problemType.placeholder"}}</option>
              {{#each this.problemTypeOptions as |opt|}}
                <option value={{opt.value}}>{{opt.label}}</option>
              {{/each}}
            </BaseSelect>
          </label>
          <label class="font-bold lg:col-span-4">
            *
            {{t "pages.contactUs.fields.description.label"}}
            <TextArea
              data-test-contact-form-description-text-area
              name="contact[problemDescription]"
              @value={{this.formData.problemDescription}}
              class="mt-2 w-full px-4 py-3 font-normal"
              rows="7"
              required="required"
              @themeKey={{this.themeKey}}
              {{on "input" this.updateProblemDescription}}
            />
          </label>
          <div class="lg:col-span-4">
            <UiButton
              data-test-submit-button
              type="submit"
              @appearance={{ButtonAppearance.regular}}
              disabled={{this.isSubmitDisabled}}
            >
              {{t "pages.contactUs.submit.label"}}
            </UiButton>
          </div>
        </form>
      </div>
    </div>
  </template>
}

export default ContactUs
